import { useCallback } from 'react';
import axios from 'axios';
import { usePill } from './searchresultpillcontext';
import { sendEventToGA } from './../utils.js';
import { getDataAccessRows, getDataAccessFtpModals, getDataAccessApiModals, getDataAccessHeliocloudS3Modals } from './pillhelpers.js';
 
/**
 * Custom hook that provides all toggle action handlers for the pill component.
 * Reads all state from PillContext — no prop drilling required.
 */
export function usePillActions() {
  const {
    hcutId,
    data, setData,
    apiData, setApiData,
    setModalData,
    isOpen, setIsOpen,
    isFtpModalOpen, setFtpModalOpen,
    isApiModalOpen, setApiModalOpen,
    isHeliocloudS3ModalOpen, setHeliocloudS3ModalOpen,
    setCurrentModalId,
  } = usePill();
 
  /**
   * Fetch dataset info from the backend.
   */
  const fetchDatasetInfo = useCallback(async (hcutIdOverride) => {
    const id = hcutIdOverride || hcutId;
    const response = await axios.get(`/heliodata/app/load_dataset_page/${id}`);
    return response.data;
  }, [hcutId]);
 
  /**
   * Toggle the dropdown open/closed.
   * Fetches data on first open. Handles both FTP/HTTP and API+S3 pill types.
   */
  const handleToggleDropdown = useCallback(async (hasS3) => {
    if (!data || data.length === 0) {
      try {
        const responseData = await fetchDatasetInfo();
 
        if (!hasS3) {
          // FTP/HTTP pill: wire up ftp row clicks
          const rows = getDataAccessRows({
            datasetData: responseData,
            isModalOpenArray: isFtpModalOpen,
            hcutId,
            onFtpClick: handleToggleFtpModal,
            onS3Click: handleToggleApiMultiModal,
            onApiClick: handleToggleApiModal,
          });
          const modals = getDataAccessFtpModals(responseData, rows.isModalOpen, setFtpModalOpen);
          setData(rows);
          setModalData(modals);
        } else {
          // API+S3 pill: wire up S3 row clicks
          const rows = getDataAccessRows({
            datasetData: responseData,
            isModalOpenArray: isHeliocloudS3ModalOpen,
            hcutId,
            onFtpClick: handleToggleFtpModal,
            onS3Click: handleToggleApiMultiModal,
            onApiClick: handleToggleApiModal,
          });
          const s3Modals = getDataAccessHeliocloudS3Modals(responseData, rows.isModalOpen, setHeliocloudS3ModalOpen);
          const apiModal = getDataAccessApiModals(responseData, false, setApiModalOpen);
          setData(rows);
          setModalData([...apiModal, ...s3Modals]);
        }
      } catch (err) {
        console.error('Failed to fetch dataset info', err);
      }
    }
    setIsOpen(prev => !prev);
  }, [
    data, hcutId, fetchDatasetInfo,
    isFtpModalOpen, isHeliocloudS3ModalOpen,
    setData, setModalData, setIsOpen,
    setFtpModalOpen, setApiModalOpen, setHeliocloudS3ModalOpen,
  ]);
 
  /**
   * Open the FTP instructions modal for a given FTP URL.
   */
  const handleToggleFtpModal = useCallback(async (ftpId) => {
    if (!data || data.length === 0) {
      try {
        const responseData = await fetchDatasetInfo();
        const rows = getDataAccessRows({
          datasetData: responseData,
          isModalOpenArray: { ...isFtpModalOpen, [ftpId]: true },
          hcutId,
          modalId: ftpId,
          onFtpClick: handleToggleFtpModal,
          onS3Click: handleToggleApiMultiModal,
          onApiClick: handleToggleApiModal,
        });
        const modals = getDataAccessFtpModals(responseData, rows.isModalOpen, setFtpModalOpen);
        setData(rows);
        setModalData(modals);
        setFtpModalOpen({ ...rows.isModalOpen });
      } catch (err) {
        console.error('Failed to fetch FTP modal data', err);
      }
    }
    setCurrentModalId(ftpId);
    setIsOpen(false);
    sendEventToGA('search_result__search_result_table_ftp_pill__button', 'ftp', ftpId);
  }, [
    data, hcutId, isFtpModalOpen, fetchDatasetInfo,
    setData, setModalData, setFtpModalOpen, setCurrentModalId, setIsOpen,
  ]);
 
  /**
   * Open the API instructions modal (single API, no S3).
   */
  const handleToggleApiModal = useCallback(async () => {
    if (!apiData) {
      try {
        const responseData = await fetchDatasetInfo();
        const rows = getDataAccessRows({
          datasetData: responseData,
          isModalOpenArray: isApiModalOpen,
          hcutId,
          onFtpClick: handleToggleFtpModal,
          onS3Click: handleToggleApiMultiModal,
          onApiClick: handleToggleApiModal,
        });
        const modals = getDataAccessApiModals(responseData, true, setApiModalOpen);
        setData(rows);
        setModalData(modals);
        setApiData(responseData);
      } catch (err) {
        console.error('Failed to fetch API modal data', err);
      }
    }
    setApiModalOpen({ api: true });
    setCurrentModalId('api');
    setIsOpen(false);
    sendEventToGA('search_result__search_result_table_api_pill_button', 'api', hcutId);
  }, [
    apiData, hcutId, isApiModalOpen, fetchDatasetInfo,
    setData, setModalData, setApiData, setApiModalOpen, setCurrentModalId, setIsOpen,
  ]);
 
  /**
   * Open the HelioCloud S3 instructions modal for a given S3 URL.
   * Also resets the API modal state before opening the S3 modal.
   */
  const handleToggleApiMultiModal = useCallback(async (s3Url) => {
    if (!data || data.length === 0) {
      try {
        const responseData = await fetchDatasetInfo();
        const rows = getDataAccessRows({
          datasetData: responseData,
          isModalOpenArray: { ...isHeliocloudS3ModalOpen, [s3Url]: true },
          hcutId,
          modalId: s3Url,
          onFtpClick: handleToggleFtpModal,
          onS3Click: handleToggleApiMultiModal,
          onApiClick: handleToggleApiModal,
        });
        const s3Modals = getDataAccessHeliocloudS3Modals(responseData, rows.isModalOpen, setHeliocloudS3ModalOpen);
        const apiModal = getDataAccessApiModals(responseData, false, setApiModalOpen);
        setData(rows);
        setModalData([...apiModal, ...s3Modals]);
        setHeliocloudS3ModalOpen({ ...rows.isModalOpen });
      } catch (err) {
        console.error('Failed to fetch S3 modal data', err);
      }
    }
    setCurrentModalId(s3Url);
    setIsOpen(false);
    setApiModalOpen({ api: false });
    setHeliocloudS3ModalOpen(prev => ({ ...prev, [s3Url]: true }));
    sendEventToGA('search_result__search_result_table_api_pill__button', 'api', s3Url);
  }, [
    data, hcutId, isHeliocloudS3ModalOpen, fetchDatasetInfo,
    setData, setModalData, setHeliocloudS3ModalOpen, setApiModalOpen, setCurrentModalId, setIsOpen,
  ]);
 
  return {
    handleToggleDropdown,
    handleToggleFtpModal,
    handleToggleApiModal,
    handleToggleApiMultiModal,
  };
}
